/**
 * FeatureComparisonTable — visual comparison matrix of features across plan tiers
 */

import React, { useState, useEffect } from 'react';
import { useTranslation } from 'react-i18next';
import { getPlanFeatureMatrix } from '../services/api';
import './FeatureComparisonTable.css';

// Fallback feature matrix when API is unavailable (v2 tiers)
const FALLBACK_MATRIX = {
  categories: [
    {
      key: 'menu',
      features: [
        { key: 'menuItems', limit: true },
        { key: 'categories', limit: false },
        { key: 'modifiers', limit: false },
        { key: 'menuSchedules', limit: false },
        { key: 'multiLanguageMenu', limit: false },
      ],
    },
    {
      key: 'orders',
      features: [
        { key: 'monthlyOrders', limit: true },
        { key: 'dineIn', limit: false },
        { key: 'takeaway', limit: false },
        { key: 'delivery', limit: false },
        { key: 'tableManagement', limit: false },
      ],
    },
    {
      key: 'operations',
      features: [
        { key: 'users', limit: true },
        { key: 'kds', limit: false },
        { key: 'analytics', limit: false },
        { key: 'advancedAnalytics', limit: false },
        { key: 'expenseTracking', limit: false },
        { key: 'multiLocation', limit: false },
      ],
    },
    {
      key: 'integrations',
      features: [
        { key: 'qrMenus', limit: false },
        { key: 'payments', limit: false },
        { key: 'deliveryPlatforms', limit: false },
        { key: 'api', limit: false },
        { key: 'whiteLabel', limit: false },
      ],
    },
    {
      key: 'support',
      features: [
        { key: 'email', limit: false },
        { key: 'chat', limit: false },
        { key: 'phone', limit: false },
        { key: 'dedicatedManager', limit: false },
      ],
    },
  ],
  plans: {
    free: {
      menu: {
        menuItems: '20',
        categories: true,
        modifiers: false,
        menuSchedules: false,
        multiLanguageMenu: false,
      },
      orders: {
        monthlyOrders: '100',
        dineIn: true,
        takeaway: false,
        delivery: false,
        tableManagement: false,
      },
      operations: {
        users: '1',
        kds: false,
        analytics: false,
        advancedAnalytics: false,
        expenseTracking: false,
        multiLocation: false,
      },
      integrations: {
        qrMenus: true,
        payments: false,
        deliveryPlatforms: false,
        api: false,
        whiteLabel: false,
      },
      support: { email: true, chat: false, phone: false, dedicatedManager: false },
    },
    starter: {
      menu: {
        menuItems: '100',
        categories: true,
        modifiers: true,
        menuSchedules: false,
        multiLanguageMenu: false,
      },
      orders: {
        monthlyOrders: '500',
        dineIn: true,
        takeaway: true,
        delivery: false,
        tableManagement: true,
      },
      operations: {
        users: '3',
        kds: true,
        analytics: true,
        advancedAnalytics: false,
        expenseTracking: false,
        multiLocation: false,
      },
      integrations: {
        qrMenus: true,
        payments: true,
        deliveryPlatforms: false,
        api: false,
        whiteLabel: false,
      },
      support: { email: true, chat: true, phone: false, dedicatedManager: false },
    },
    professional: {
      menu: {
        menuItems: '500',
        categories: true,
        modifiers: true,
        menuSchedules: true,
        multiLanguageMenu: true,
      },
      orders: {
        monthlyOrders: '5000',
        dineIn: true,
        takeaway: true,
        delivery: true,
        tableManagement: true,
      },
      operations: {
        users: '10',
        kds: true,
        analytics: true,
        advancedAnalytics: true,
        expenseTracking: true,
        multiLocation: false,
      },
      integrations: {
        qrMenus: true,
        payments: true,
        deliveryPlatforms: true,
        api: false,
        whiteLabel: false,
      },
      support: { email: true, chat: true, phone: true, dedicatedManager: false },
    },
    business: {
      menu: {
        menuItems: '-1',
        categories: true,
        modifiers: true,
        menuSchedules: true,
        multiLanguageMenu: true,
      },
      orders: {
        monthlyOrders: '20000',
        dineIn: true,
        takeaway: true,
        delivery: true,
        tableManagement: true,
      },
      operations: {
        users: '25',
        kds: true,
        analytics: true,
        advancedAnalytics: true,
        expenseTracking: true,
        multiLocation: true,
      },
      integrations: {
        qrMenus: true,
        payments: true,
        deliveryPlatforms: true,
        api: true,
        whiteLabel: false,
      },
      support: { email: true, chat: true, phone: true, dedicatedManager: false },
    },
    enterprise: {
      menu: {
        menuItems: '-1',
        categories: true,
        modifiers: true,
        menuSchedules: true,
        multiLanguageMenu: true,
      },
      orders: {
        monthlyOrders: '-1',
        dineIn: true,
        takeaway: true,
        delivery: true,
        tableManagement: true,
      },
      operations: {
        users: '-1',
        kds: true,
        analytics: true,
        advancedAnalytics: true,
        expenseTracking: true,
        multiLocation: true,
      },
      integrations: {
        qrMenus: true,
        payments: true,
        deliveryPlatforms: true,
        api: true,
        whiteLabel: true,
      },
      support: { email: true, chat: true, phone: true, dedicatedManager: true },
    },
  },
};

function FeatureComparisonTable({ plans, currentPlanId }) {
  const { t } = useTranslation();
  const [matrix, setMatrix] = useState(null);
  const [expandedCategories, setExpandedCategories] = useState({});

  // Plan order for table columns
  const planOrder = ['free', 'starter', 'professional', 'business', 'enterprise'];
  const orderedPlans = planOrder.map((id) => plans.find((p) => p.planId === id)).filter(Boolean);

  useEffect(() => {
    loadMatrix();
  }, []);

  const loadMatrix = async () => {
    try {
      const response = await getPlanFeatureMatrix();
      setMatrix(response);
    } catch {
      setMatrix(FALLBACK_MATRIX);
    }
    // Expand all categories by default
    const expanded = {};
    (FALLBACK_MATRIX.categories || []).forEach((cat) => {
      expanded[cat.key] = true;
    });
    setExpandedCategories(expanded);
  };

  const toggleCategory = (key) => {
    setExpandedCategories((prev) => ({ ...prev, [key]: !prev[key] }));
  };

  const renderCellValue = (value) => {
    if (value === true) {
      return <span className="comparison-check">✓</span>;
    }
    if (value === false) {
      return <span className="comparison-cross">—</span>;
    }
    // Numeric limit
    if (value === '-1') {
      return <span className="comparison-unlimited">{t('planSelection.unlimited')}</span>;
    }
    return <span className="comparison-limit">{value}</span>;
  };

  if (!matrix) {
    return null;
  }

  return (
    <div className="feature-comparison-table">
      <h2 className="comparison-title">{t('planSelection.compareFeatures')}</h2>
      <div className="comparison-table-wrapper">
        <table className="comparison-table">
          <thead>
            <tr>
              <th className="comparison-feature-header">{t('planSelection.feature')}</th>
              {orderedPlans.map((plan) => (
                <th
                  key={plan.planId}
                  className={`comparison-plan-header ${plan.planId === currentPlanId ? 'comparison-current' : ''} ${plan.planId === 'professional' ? 'comparison-recommended' : ''}`}
                >
                  <span className="comparison-plan-name">
                    {t(`planSelection.plans.${plan.planId}.name`, plan.displayName || plan.name)}
                  </span>
                </th>
              ))}
            </tr>
          </thead>
          <tbody>
            {matrix.categories.map((category) => (
              <React.Fragment key={category.key}>
                <tr
                  className="comparison-category-row"
                  onClick={() => toggleCategory(category.key)}
                  role="button"
                  tabIndex={0}
                  onKeyDown={(e) => e.key === 'Enter' && toggleCategory(category.key)}
                >
                  <td colSpan={orderedPlans.length + 1} className="comparison-category-cell">
                    <span className="comparison-category-toggle">
                      {expandedCategories[category.key] ? '▼' : '▶'}
                    </span>
                    {t(`planSelection.featureCategories.${category.key}`)}
                  </td>
                </tr>
                {expandedCategories[category.key] &&
                  category.features.map((feature) => (
                    <tr key={feature.key} className="comparison-feature-row">
                      <td className="comparison-feature-name">
                        {t(`planSelection.featureNames.${feature.key}`)}
                      </td>
                      {orderedPlans.map((plan) => {
                        const planData = matrix.plans[plan.planId];
                        const value = planData?.[category.key]?.[feature.key];
                        return (
                          <td
                            key={plan.planId}
                            className={`comparison-cell ${plan.planId === currentPlanId ? 'comparison-current' : ''} ${plan.planId === 'professional' ? 'comparison-recommended' : ''}`}
                          >
                            {renderCellValue(value)}
                          </td>
                        );
                      })}
                    </tr>
                  ))}
              </React.Fragment>
            ))}
          </tbody>
        </table>
      </div>
    </div>
  );
}

export default FeatureComparisonTable;
